<script setup lang="ts">
import { computed } from 'vue';
import { useInternational } from '@/common/locale';
import type { AlarmRecommendation, AlarmRecommendMetric } from '@/ai/utils/types';

const { t } = useInternational();

const props = defineProps<{
  rec: AlarmRecommendation;
}>();

const emit = defineEmits<{
  /** 행별 '알람 추가' — 해당 메트릭으로 알람 규칙 생성 화면을 prefill 한다. */
  (e: 'add-alarm', payload: { rec: AlarmRecommendation; metric: AlarmRecommendMetric }): void;
}>();

/** 표에 표시할 메트릭 전체. '알람 추가' 버튼 노출은 행별 v-if="m.recommend" 로 게이팅. */
const rows = computed(() => props.rec.metrics ?? []);

const fmt = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  return v.toLocaleString(undefined, { maximumFractionDigits: 1 });
};

const baselineLabel = computed(() =>
  t('WORD.CHATBOT.ALARM_REC.BASELINE', { days: props.rec.baseline_days }),
);

const onAdd = (metric: AlarmRecommendMetric) => {
  emit('add-alarm', { rec: props.rec, metric });
};
</script>

<template>
  <div class="alarm-rec">
    <div class="alarm-rec__head">
      <ev-icon
        icon="ev-icon-bell"
        class="alarm-rec__icon"
      />
      <div class="alarm-rec__title-box">
        <strong class="alarm-rec__title">{{
          t('WORD.CHATBOT.ALARM_REC.TITLE', { instance: rec.instance_name })
        }}</strong>
        <span class="alarm-rec__sub"
          >{{ baselineLabel }} · {{ t('WORD.CHATBOT.ALARM_REC.SUB_SOURCE') }}</span
        >
      </div>
    </div>

    <div class="alarm-rec__table-wrap">
      <table class="alarm-rec__table">
        <thead>
          <tr>
            <th class="alarm-rec__th alarm-rec__th--metric">{{ t('WORD.METRIC') }}</th>
            <th class="alarm-rec__th alarm-rec__th--num">{{ t('WORD.AVG') }}</th>
            <th class="alarm-rec__th alarm-rec__th--num">p95</th>
            <th class="alarm-rec__th alarm-rec__th--num">{{ t('WORD.MAX') }}</th>
            <th class="alarm-rec__th alarm-rec__th--num">
              {{ t('WORD.CHATBOT.ALARM_REC.TH_WARNING') }}
            </th>
            <th class="alarm-rec__th alarm-rec__th--num">
              {{ t('WORD.CHATBOT.ALARM_REC.TH_CRITICAL') }}
            </th>
            <th class="alarm-rec__th alarm-rec__th--action" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="m in rows"
            :key="m.data_id"
            class="alarm-rec__tr"
          >
            <td class="alarm-rec__td alarm-rec__td--metric">
              <span class="alarm-rec__metric-name">{{ m.name }}</span>
              <span
                v-if="m.unit"
                class="alarm-rec__unit"
                >{{ m.unit }}</span
              >
              <span
                v-if="m.direction === 'lower_worse'"
                class="alarm-rec__dir"
                :title="t('WORD.CHATBOT.ALARM_REC.DANGER_LOW_TOOLTIP')"
                >{{ t('WORD.CHATBOT.ALARM_REC.DANGER_LOW') }}</span
              >
            </td>
            <td class="alarm-rec__td alarm-rec__td--num">{{ fmt(m.baseline?.avg) }}</td>
            <td class="alarm-rec__td alarm-rec__td--num">{{ fmt(m.baseline?.p95) }}</td>
            <td class="alarm-rec__td alarm-rec__td--num">{{ fmt(m.baseline?.max) }}</td>
            <template v-if="m.recommend">
              <td class="alarm-rec__td alarm-rec__td--num alarm-rec__td--warn">
                {{ m.recommend.warningComparison }} {{ fmt(m.recommend.warning) }}
              </td>
              <td class="alarm-rec__td alarm-rec__td--num alarm-rec__td--crit">
                {{ m.recommend.criticalComparison }} {{ fmt(m.recommend.critical) }}
              </td>
              <td class="alarm-rec__td alarm-rec__td--action">
                <button
                  type="button"
                  class="alarm-rec__add-btn"
                  :title="t('WORD.CHATBOT.ALARM_REC.ADD_ALARM_TOOLTIP')"
                  @click="onAdd(m)"
                >
                  {{ t('WORD.CHATBOT.ALARM_REC.ADD_ALARM') }}
                </button>
              </td>
            </template>
            <template v-else>
              <td
                class="alarm-rec__td alarm-rec__td--note"
                colspan="3"
              >
                {{ m.recommend_note || t('WORD.CHATBOT.ALARM_REC.NO_DATA_NOTE') }}
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <p class="alarm-rec__foot">
      {{ t('WORD.CHATBOT.ALARM_REC.FORMULA') }}
      {{ t('WORD.CHATBOT.ALARM_REC.FORMULA_NOTE') }}
    </p>
  </div>
</template>

<style scoped lang="scss">
.alarm-rec {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 92%;
  padding: 12px 14px;
  border: 1px solid var(--color-gray-05-03);
  border-radius: 12px;
  background: var(--color-gray-07-00);
  box-shadow: 0 10px 26px 0 var(--ai-chat-bubble-shadow-color);

  &__head {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__icon {
    width: 16px;
    height: 16px;
    background-color: var(--color-blue-04-04);
  }

  &__title-box {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }

  &__title {
    font-size: 13px;
    font-weight: 700;
    color: var(--color-gray-00-11);
  }

  &__sub {
    font-size: 11px;
    color: var(--color-gray-02-11);
  }

  &__table-wrap {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 11.5px;
  }

  &__th {
    padding: 5px 8px;
    border-bottom: 1px solid var(--color-gray-05-03);
    font-weight: 600;
    color: var(--color-gray-02-11);
    text-align: right;
    white-space: nowrap;

    &--metric {
      text-align: left;
    }

    &--action {
      width: 1%;
    }
  }

  &__td {
    padding: 5px 8px;
    border-bottom: 1px solid var(--color-gray-06-02);
    color: var(--color-gray-00-11);
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    &--metric {
      display: flex;
      gap: 6px;
      align-items: baseline;
      text-align: left;
    }

    &--warn {
      font-weight: 600;
      color: var(--tag-orange-font-color, #b06b00);
    }

    &--crit {
      font-weight: 600;
      color: var(--connection-test-fail-color, #d32f2f);
    }

    &--note {
      font-style: italic;
      color: var(--color-gray-03-11);
      text-align: left;
    }
  }

  &__metric-name {
    font-weight: 500;
  }

  &__unit {
    font-size: 10px;
    color: var(--color-gray-03-11);
  }

  &__dir {
    padding: 0 5px;
    border-radius: 8px;
    background: var(--color-gray-08-01);
    font-size: 9.5px;
    color: var(--color-blue-04-04);
  }

  &__add-btn {
    padding: 3px 10px;
    cursor: pointer;
    border: 1px solid var(--color-blue-04-04);
    border-radius: 999px;
    background: transparent;
    font-size: 11px;
    font-weight: 500;
    color: var(--color-blue-04-04);
    white-space: nowrap;
    transition:
      background 0.15s ease,
      color 0.15s ease;

    &:hover {
      background: var(--color-blue-04-04);
      color: #ffffff;
    }
    &:active {
      transform: translateY(1px);
    }
  }

  &__foot {
    margin: 0;
    font-size: 10.5px;
    line-height: 1.45;
    color: var(--color-gray-03-11);
  }
}
</style>
